import { CSSProperties } from 'react';

/**
 * Layout styles for a multi-table GRID cell's GameInner root. Extracted from
 * Game.tsx so the "felt never resizes on the player's turn" invariant is
 * unit-testable in isolation (see gameGridLayout.test.ts) without importing
 * GameInner's full runtime (gsap / sounds / sockets / the whole table tree).
 *
 * The cell is a fixed-height flex COLUMN: [felt (flex:1)] then [footer (64px)].
 *
 * Do NOT add `overflow: hidden` to this column. The EN grid "everything jumps
 * up on the player's turn" bug (tournament AND cash) was caused by exactly that
 * clip: with the fixed-height column clipped, on the player's turn the taller
 * action content forced the flex:1 felt to shrink to fit; the felt's
 * ResizeObserver then re-measured/re-scaled to the smaller size and the
 * percentage-anchored on-felt details + seats + action bar visibly jumped up.
 * Verified in-browser — removing this clip (or toggling `display:flex`, i.e.
 * any forced relayout) fixes it.
 *
 * NO BACKGROUND OF ITS OWN (design call, Jason 2026-08-15). The cell used to
 * paint `linear-gradient(180deg, #1c1a14, #12100c)` — the SAME gradient the
 * page/room behind it paints, and the same one single-table view uses
 * (gamePageLayout `pageShell`). Painting it twice is what made the grid look
 * like it had two browns: each copy completes its full range over its OWN
 * height (a 807px cell vs a 900px room), so at every y the two values differ
 * slightly and the seam reads as a lighter rectangle behind each table. There
 * is only ever one brown in this product; letting the room's single gradient
 * show through is what makes four tables share one continuous background
 * instead of sitting in four tinted boxes.
 */
export const gridCellStyle: CSSProperties = {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
  color: 'white'
};

// Felt area of a grid cell. flex:1 fills the space above the footer, and
// minHeight:0 lets it shrink cleanly (a flex item's default min-height:auto
// would otherwise resist shrinking and let content blow the layout out). The
// table oval and its fixed-position overlays (game-info, control rail) live
// here, so keeping this box's size driven ONLY by the cell — not by the action
// footer — is what stops the action UI from reshaping the table above it.
/**
 * NOT CLIPPED (design call, Jason 2026-08-15). A seat's turn glow is painted by
 * the seat, which sits ON the felt's rim — with `overflow: hidden` here the
 * glow was sliced off at a hard vertical line, which is the single strongest
 * tell that these are four boxes rather than four tables in one room.
 *
 * What makes this safe is the row's 16px gap (MultiTableGrid `gridStyle`): the
 * glow now falls off into empty room instead of landing on a neighbour. The
 * carousel row is still the outer clip, so nothing escapes the grid itself.
 */
export const gridFeltStyle: CSSProperties = {
  flex: 1,
  minHeight: 0,
  position: 'relative'
};

// The action footer is a FIXED-height band, never grown or shrunk by its
// content. Player-action UI (Fold/Check/Bet, pre-action checkboxes, etc.) all
// fit within this height, and the bet/raise tray expands UPWARD as an overlay
// (absolute, bottom:100%) rather than adding height — so the felt above never
// resizes when actions appear/change, which previously distorted the table and
// let the fixed-position game-info / control rail overlap the compressed felt.
//
// `minHeight: 0` defeats a flex item's default `min-height: auto` (= content
// min-content), so a taller action row on the player's turn can't force this
// band past its 64px basis and steal height from the flex:1 felt above. Belt-
// and-suspenders alongside the real fix (gridCellStyle no longer clips, so the
// felt is never forced to shrink) — the band stays exactly GRID_FOOTER_HEIGHT
// and any rare overflow rides upward over the felt (like the raise tray).
export const GRID_FOOTER_HEIGHT = 64;
export const gridFooterStyle: CSSProperties = {
  flex: `0 0 ${GRID_FOOTER_HEIGHT}px`,
  height: GRID_FOOTER_HEIGHT,
  minHeight: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(18, 16, 12, 0.95)',
  borderTop: '1px solid rgba(255, 255, 255, 0.06)',
  padding: '0.2rem 0.3rem',
  gap: '0.25rem'
};
